<template>
  <div class="input-action-status">
    <template v-if="displayState === StatusDisplayState.itemDetails">
      <OrderItems :items=items :key="'action-status-' + input.id.toString()" :input="input" @submit="done()" @back="goBack">
        <StepTitle hr>
          <div class="how-can">Select Items for Status</div>
        </StepTitle>
      </OrderItems>
    </template>
    <template v-else>
      <StepMedia :session="activeSession" />
      <SessionContent></SessionContent>
      <b-row class="justify-content-center" :class="{'new-btns-container': !isLegacy, 'boxes': isLegacy}" v-if="showPopupBackButton || isLastItem || !activeSession.enhanced_mode">
        <b-col v-if="isLastItem" :cols="!isLegacy ? 12 : 10" md="8" :lg="!isLegacy ? 6 : 3" class="mb-2">
          <button @click="setDisplayState(StatusDisplayState.itemDetails)" :class="buttonStyle">
            View Details
          </button>
        </b-col>
        <b-col v-if="isLastItem || !activeSession.enhanced_mode" :cols="!isLegacy ? 12 : 10" md="8" :lg="!isLegacy ? 6 : 3" class="mb-2">
          <button @click="done()" class="mt-0" :class="buttonStyle">
            <span class="inline-button-label" v-html="input.formatted_label"></span>&nbsp;
          </button>
        </b-col>
        <b-col v-if="returnLabelText" :cols="!isLegacy ? 12 : 10" md="8" :lg="!isLegacy ? 6 : 3" class="mb-2">
          <button @click="getShipmentReturnLabel(false)" class="mt-0" :class="buttonStyle" v-html="returnLabelText"></button>
        </b-col>
        <b-col v-if="returnQRCodeText" :cols="!isLegacy ? 12 : 10" md="8" :lg="!isLegacy ? 6 : 3" class="mb-2">
          <button @click="getShipmentReturnLabel(true)" class="mt-0" :class="buttonStyle" v-html="returnQRCodeText"></button>
        </b-col>
        <b-col v-if="showPopupBackButton" :cols="!isLegacy ? 12 : 10" md="8" :lg="!isLegacy ? 6 : 3" class="mb-2">
          <button @click="goBack()" class="mt-0" :class="backButtonStyle">Back</button>
        </b-col >
      </b-row>
    </template>
    <b-link v-if="canShowCloseIcon" block class="centered-block close-button" @click="closePopup()" variant="secondary">&times;</b-link>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {InputType} from '@/constants'
import {recalculateFontSizes} from '@/utils'
import StepMedia from '@/components/StepMedia.vue'
const StepTitle = () => import('./StepTitle')
const SessionContent = () => import('./SessionContent')
const OrderItems = () => import('@/components/OrderItems')

const StatusDisplayState = Object.freeze({
  status: 0,
  itemDetails: 1,
  destroyed: 2
})

export default {
  name: 'InputActionStatus',
  components: {
    StepMedia,
    SessionContent,
    StepTitle,
    OrderItems
  },
  props: {
    input: Object
  },
  data: function() {
    return {
      displayState: StatusDisplayState.status,
      StatusDisplayState,
      slide: 0,
      items: [],
      returnLabelText: null,
      returnQRCodeText: null
    }
  },
  computed: {
    ...mapState(['popupDisplayState']),
    ...mapGetters(['isLastItem', 'canShowCloseIcon', 'showPopupBackButton', 'popupCloseCanUndo',
      'popupCloseCanSelectFirstInput', 'activeSession']),
    isLegacy: function() {
      return this.activeSession && this.activeSession.is_legacy
    },
    buttonStyle: function() {
      return !this.isLegacy ? 'btn-base input-choice-btn mb-0' : 'btn btn-block btn-primary btn-account'
    },
    backButtonStyle: function() {
      return !this.isLegacy ? 'btn-base new-btn-back new-btn-outline-success mb-0' : 'btn btn-block btn-back btn-outline-primary btn-outline-success'
    }
  },
  methods: {
    done: function() {
      this.$emit('done') // we're done with the item status view, move on to the next step
    },
    goBack: function() {
      if (this.displayState === StatusDisplayState.status) {
        this.$emit('back') // we're viewing the status of the input action, go back to the previous page
      } else {
        this.displayState = StatusDisplayState.status // we're viewing the selected item details, go back to status
      }
    },
    closePopup: function() {
      if (this.popupCloseCanUndo) {
        this.$emit('back')
      } else if (this.popupCloseCanSelectFirstInput) {
        this.done()
      }
    },
    setDisplayState: function(displayState) {
      this.displayState = displayState
    },
    editItem: function(input, item) {
      this.$emit('edit', input, item)
    },
    hideInputTypeButton: function(input) {
      // if input is scheduled on editor, we need to hide the button from the editor content
      setTimeout(() => {
        const elementFromEditor = document.querySelector("[inputtype='" + input.type + "']")
        if (elementFromEditor) {
          elementFromEditor.style.display = 'none'
        }
      }, 100)
    },
    processAdditionalInputs: function() {
      this.activeSession.additional_inputs.forEach(input => {
        if (input.type === InputType.ShipmentReturnLabel) {
          this.returnLabelText = input.formatted_label
          this.hideInputTypeButton(input)
        } else if (input.type === InputType.ShipmentReturnQRCode) {
          this.returnQRCodeText = input.formatted_label
          this.hideInputTypeButton(input)
        }
      })
    },
    getShipmentReturnLabel: function(qrCode) {
      if (this.activeSession.selected_item) {
        this.$store.commit('busy')
        let newWindow = window.open('', '_blank')
        this.$store.dispatch('load_shipment_return_label',
          {item: this.activeSession.selected_item, qr_code: qrCode, newWindow})
      }
    }
  },
  destroyed() {
    if (this.popupDisplayState) {
      this.$store.commit('set_display_state', StatusDisplayState.destroyed)
    }
  },
  updated() {
    if (!this.isLegacy) {
      recalculateFontSizes()
    }
  },
  created() {
    this.items = [this.activeSession.selected_item]

    if (this.activeSession.new_item) {
      this.items.push(this.activeSession.new_item)
    }

    if (this.popupDisplayState) {
      this.displayState = this.popupDisplayState
    }

    if (this.activeSession.additional_inputs) {
      this.processAdditionalInputs()
    }
  }
}
</script>

<style scoped>

</style>
